import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import {
  makeStyles, FormControl, InputLabel, Select, MenuItem,
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';

import { ProgramType } from '../utils/programs';
import {
  selectOrganizations, loadOrganizations,
} from '../store/slices/organizationsSlice';

export interface AdminFilters {
  organizationId?: number;
  program?: ProgramType;
  registrationDateMin?: Date;
  registrationDateMax?: Date;
}

export interface AdminFiltersControlProps {
  filters: AdminFilters;
  onChange: (filters: AdminFilters) => any;
  classes?: Partial<ReturnType<typeof useStyles>>;
}

export default function AdminFiltersControl(props: AdminFiltersControlProps) {
  const { filters, onChange } = props;

  const classes = useStyles(props);
  const { getAccessTokenSilently } = useAuth0();

  const { organizations, loading, error } = useSelector(selectOrganizations);
  const dispatch = useDispatch();

  useEffect(() => {
    if (organizations == null) {
      dispatch(loadOrganizations(getAccessTokenSilently));
    }
  }, []);

  return (
    <div className={classes.container}>
      <FormControl className={classes.formControl}>
        <InputLabel id="filter-organization-label">Organization</InputLabel>
        <Select
          labelId="filter-organization-label"
          value={filters.organizationId || 'all'}
          onChange={(event) => onChange({
            ...filters,
            organizationId: event.target.value === 'all'
              ? undefined : event.target.value as number,
          })}
        >
          <MenuItem value="all">All</MenuItem>
          {error && (
            <MenuItem disabled>Error loading organizations...</MenuItem>
          )}
          {loading && (
            <MenuItem disabled>Loading...</MenuItem>
          )}
          {(organizations || []).map((organization) => (
            <MenuItem key={organization.id} value={organization.id}>
              {organization.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl className={classes.formControl}>
        <InputLabel id="filter-program-label">Program</InputLabel>
        <Select
          labelId="filter-program-label"
          value={filters.program || 'all'}
          onChange={(event) => onChange({
            ...filters,
            program: event.target.value === 'all'
              ? undefined : event.target.value as ProgramType,
          })}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value={ProgramType.LowBack}>Low Back</MenuItem>
          <MenuItem value={ProgramType.Shoulder}>Shoulder</MenuItem>
          <MenuItem value={ProgramType.Knee}>Knee</MenuItem>
          <MenuItem value={ProgramType.Hip}>Hip</MenuItem>
          <MenuItem value={ProgramType.Neck}>Neck</MenuItem>
        </Select>
      </FormControl>
      <DatePicker
        // variant="inline"
        emptyLabel="None"
        disableFuture
        clearable
        format="MM/dd/yyyy"
        label="Start Date"
        value={filters.registrationDateMin || null}
        onChange={(registrationDateMin: Date | null) => onChange({
          ...filters, registrationDateMin: registrationDateMin || undefined,
        })}
        className={classes.formControl}
      />
      <DatePicker
        // variant="inline"
        emptyLabel="None"
        disableFuture
        clearable
        format="MM/dd/yyyy"
        label="End Date"
        value={filters.registrationDateMax || null}
        onChange={(registrationDateMax: Date | null) => onChange({
          ...filters, registrationDateMax: registrationDateMax || undefined,
        })}
        className={classes.formControl}
      />
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    flexDirection: 'row',
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-1),
  },
  formControl: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    minWidth: 120,
  },
}));
